import React from 'react';
import { Typography, Card, Space, Divider, Alert, List, Table } from 'antd';
import { 
  FileTextOutlined, 
  CheckCircleOutlined, 
  ExclamationCircleOutlined, 
  DollarOutlined,
  ToolOutlined,
  WarningOutlined,
  LockOutlined,
  PhoneOutlined,
  MailOutlined,
  GlobalOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const Terms = () => {
  // Données pour le tableau des plans
  const pricingData = [
    {
      key: '1',
      plan: 'Découverte',
      price: 'Gratuit',
      messages: '100/mois',
      features: 'Fonctionnalités de base'
    },
    {
      key: '2',
      plan: 'Croissance',
      price: '15 000 XOF/mois',
      messages: '1 000/mois',
      features: 'Points IA, intégrations'
    },
    {
      key: '3',
      plan: 'Professionnel',
      price: '35 000 XOF/mois',
      messages: '5 000/mois',
      features: 'Toutes fonctionnalités'
    },
    {
      key: '4',
      plan: 'Entreprise',
      price: '75 000 XOF/mois',
      messages: 'Illimité',
      features: 'Support prioritaire'
    }
  ];

  const columns = [
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Prix',
      dataIndex: 'price',
      key: 'price',
    },
    {
      title: 'Messages',
      dataIndex: 'messages',
      key: 'messages',
    },
    {
      title: 'Fonctionnalités',
      dataIndex: 'features',
      key: 'features',
    }
  ];

  return (
    <div className="terms-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-400 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <FileTextOutlined className="text-6xl mb-6" />
          <Title level={1} className="text-white mb-6">
            Conditions d'Utilisation
          </Title>
          <Paragraph className="text-lg opacity-90 max-w-2xl mx-auto">
            Ces conditions d'utilisation régissent votre accès et utilisation de la plateforme Mercuri Shop. 
            Veuillez les lire attentivement avant d'utiliser nos services.
          </Paragraph>
          <Text className="text-sm opacity-75">
            Dernière mise à jour : 28 janvier 2025
          </Text>
        </div>
      </section>

      {/* Contenu principal */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          
          {/* Acceptation des conditions */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <CheckCircleOutlined className="text-blue-500" />
              1. Acceptation des conditions
            </Title>
            <Paragraph className="text-base">
              En accédant à la plateforme Mercuri Shop ou en l'utilisant, vous acceptez d'être lié par ces conditions d'utilisation. 
              Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser nos services.
            </Paragraph>
            <Paragraph className="text-base">
              Ces conditions s'appliquent à tous les utilisateurs de Mercuri Shop, y compris les visiteurs, 
              les utilisateurs inscrits et les clients payants.
            </Paragraph>
            
            <Alert
              message="Important"
              description="L'utilisation de Mercuri Shop implique l'acceptation automatique de ces conditions ainsi que de notre Politique de Confidentialité."
              type="warning"
              showIcon
              className="mt-4"
            />
          </Card>

          {/* Description du service */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <ToolOutlined className="text-blue-500" />
              2. Description du service
            </Title>
            
            <Paragraph className="text-base">
              Mercuri Shop est une plateforme SaaS (Software as a Service) qui permet aux entreprises de gérer 
              leurs commandes, leurs livraisons et leurs conversations WhatsApp, avec des outils d'intelligence artificielle 
              pour l'analyse et la création de contenus.
            </Paragraph>

            <Title level={4} className="mt-6 mb-3">2.1 Fonctionnalités principales</Title>
            <List
              size="small"
              dataSource={[
                'Assistant commercial et analyses par IA',
                'Gestion centralisée des conversations',
                'Intégration avec des plateformes e-commerce (Shopify, etc.)',
                'Système de Point de Vente (POS) intégré',
                'Gestion des commandes et livraisons',
                'Analyses et rapports détaillés',
                'Gestion multi-utilisateurs et caissiers'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">2.2 Disponibilité du service</Title>
            <Paragraph className="text-base">
              Nous nous efforçons de maintenir une disponibilité de 99,5% de nos services. 
              Cependant, nous ne pouvons garantir un accès ininterrompu en raison de :
            </Paragraph>
            <List
              size="small"
              dataSource={[
                'Maintenances programmées (notifiées à l\'avance)',
                'Pannes techniques imprévisibles',
                'Problèmes liés aux services tiers (WhatsApp, hébergeurs)',
                'Cas de force majeure'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />
          </Card>

          {/* Inscription et comptes */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <LockOutlined className="text-blue-500" />
              3. Inscription et comptes utilisateur
            </Title>
            
            <Title level={4} className="mt-6 mb-3">3.1 Création de compte</Title>
            <Paragraph className="text-base">
              Pour utiliser Mercuri Shop, vous devez créer un compte en fournissant des informations exactes et complètes. 
              Vous êtes responsable de :
            </Paragraph>
            <List
              size="small"
              dataSource={[
                'La véracité des informations fournies',
                'La mise à jour de vos informations personnelles',
                'La confidentialité de vos identifiants de connexion',
                'Toutes les activités effectuées sous votre compte'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">3.2 Sécurité du compte</Title>
            <List
              size="small"
              dataSource={[
                'Utilisez un mot de passe fort et unique',
                'Activez l\'authentification à deux facteurs si disponible',
                'Ne partagez jamais vos identifiants avec des tiers',
                'Signalez immédiatement tout usage non autorisé'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">3.3 Suspension et résiliation</Title>
            <Paragraph className="text-base">
              Nous nous réservons le droit de suspendre ou résilier votre compte en cas de :
            </Paragraph>
            <List
              size="small"
              dataSource={[
                'Violation de ces conditions d\'utilisation',
                'Activité frauduleuse ou illégale',
                'Non-paiement des frais dus',
                'Utilisation abusive de nos services',
                'Demande des autorités compétentes'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />
          </Card>

          {/* Plans et tarification */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <DollarOutlined className="text-blue-500" />
              4. Plans et tarification
            </Title>
            
            <Paragraph className="text-base">
              Mercuri Shop propose plusieurs plans tarifaires adaptés aux besoins de chaque entreprise :
            </Paragraph>

            <Table 
              dataSource={pricingData} 
              columns={columns} 
              pagination={false}
              className="mt-4 mb-6"
              size="middle"
            />

            <Title level={4} className="mt-6 mb-3">4.1 Facturation</Title>
            <List
              size="small"
              dataSource={[
                'Les frais sont facturés mensuellement à l\'avance',
                'Le paiement est dû à la date d\'échéance indiquée',
                'Les prix sont exprimés en Francs CFA (XOF), taxes incluses',
                'Aucun remboursement pour les périodes non utilisées',
                'Les dépassements de quota sont facturés selon la grille tarifaire'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">4.2 Modifications tarifaires</Title>
            <Paragraph className="text-base">
              Nous nous réservons le droit de modifier nos tarifs avec un préavis de 30 jours. 
              Les nouveaux tarifs s'appliqueront à votre prochaine période de facturation.
            </Paragraph>

            <Title level={4} className="mt-6 mb-3">4.3 Retard de paiement</Title>
            <Paragraph className="text-base">
              En cas de retard de paiement :
            </Paragraph>
            <List
              size="small"
              dataSource={[
                'Suspension du service après 7 jours de retard',
                'Suppression des données après 30 jours (sauf obligation légale)',
                'Frais de recouvrement selon la législation en vigueur'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />
          </Card>

          {/* Utilisation acceptable */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <ExclamationCircleOutlined className="text-orange-500" />
              5. Utilisation acceptable
            </Title>
            
            <Title level={4} className="mt-6 mb-3">5.1 Utilisations autorisées</Title>
            <List
              size="small"
              dataSource={[
                'Communication commerciale légale avec vos clients',
                'Support client et service après-vente',
                'Notifications de commandes et livraisons',
                'Marketing avec consentement préalable des destinataires',
                'Automatisation de processus métier légitimes'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">5.2 Utilisations interdites</Title>
            <Alert
              message="Strictement interdites"
              description="Les utilisations suivantes entraîneront la suspension immédiate de votre compte :"
              type="error"
              showIcon
              className="mb-4"
            />
            
            <List
              size="small"
              dataSource={[
                'Spam ou messages non sollicités en masse',
                'Harcèlement, menaces ou contenu offensant',
                'Diffusion de contenu illégal, haineux ou discriminatoire',
                'Usurpation d\'identité ou fraude',
                'Violation des droits de propriété intellectuelle',
                'Tentative de piratage ou d\'intrusion',
                'Revente ou redistribution de nos services sans autorisation',
                'Utilisation pour des activités illégales'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text className="text-red-600">• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">5.3 Respect des réglementations</Title>
            <Paragraph className="text-base">
              Vous vous engagez à respecter :
            </Paragraph>
            <List
              size="small"
              dataSource={[
                'Les conditions d\'utilisation de WhatsApp Business',
                'Les lois sur la protection des données (RGPD, etc.)',
                'Les réglementations locales sur le marketing direct',
                'Les lois sur le commerce électronique',
                'Toute autre réglementation applicable'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />
          </Card>

          {/* Propriété intellectuelle */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <LockOutlined className="text-blue-500" />
              6. Propriété intellectuelle
            </Title>
            
            <Title level={4} className="mt-6 mb-3">6.1 Nos droits</Title>
            <Paragraph className="text-base">
              Mercuri Shop et tous ses composants (logiciel, interface, contenu, marques) sont protégés par 
              les droits de propriété intellectuelle. Vous ne pouvez pas :
            </Paragraph>
            <List
              size="small"
              dataSource={[
                'Copier, modifier ou distribuer notre logiciel',
                'Effectuer de l\'ingénierie inverse',
                'Utiliser nos marques sans autorisation',
                'Créer des œuvres dérivées',
                'Retirer les mentions de propriété'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">6.2 Vos droits</Title>
            <Paragraph className="text-base">
              Vous conservez tous les droits sur vos données et contenus. En utilisant Mercuri Shop, 
              vous nous accordez une licence limitée pour traiter vos données dans le cadre de nos services.
            </Paragraph>

            <Title level={4} className="mt-6 mb-3">6.3 Respect des droits tiers</Title>
            <Paragraph className="text-base">
              Vous garantissez que vos contenus ne violent aucun droit de propriété intellectuelle de tiers. 
              Vous nous indemniserez contre toute réclamation à ce sujet.
            </Paragraph>
          </Card>

          {/* Limitation de responsabilité */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <WarningOutlined className="text-orange-500" />
              7. Limitation de responsabilité
            </Title>
            
            <Alert
              message="Clause importante"
              description="Cette section limite notre responsabilité dans les limites autorisées par la loi."
              type="warning"
              showIcon
              className="mb-6"
            />

            <Title level={4} className="mt-6 mb-3">7.1 Exclusions de garantie</Title>
            <Paragraph className="text-base">
              Nos services sont fournis "en l'état" sans garantie expresse ou implicite. 
              Nous ne garantissons pas :
            </Paragraph>
            <List
              size="small"
              dataSource={[
                'Le fonctionnement ininterrompu des services',
                'L\'absence d\'erreurs ou de bugs',
                'La compatibilité avec tous les systèmes',
                'L\'obtention de résultats spécifiques',
                'La sécurité absolue contre les cyberattaques'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">7.2 Limitation des dommages</Title>
            <Paragraph className="text-base">
              Notre responsabilité totale envers vous ne peut excéder le montant des frais 
              que vous avez payés au cours des 12 derniers mois.
            </Paragraph>

            <Title level={4} className="mt-6 mb-3">7.3 Exclusions</Title>
            <Paragraph className="text-base">
              Nous ne sommes pas responsables des dommages indirects, accessoires, 
              spéciaux ou consécutifs, y compris :
            </Paragraph>
            <List
              size="small"
              dataSource={[
                'Perte de profits ou de revenus',
                'Perte de données ou d\'informations',
                'Interruption d\'activité',
                'Perte de clientèle ou de réputation',
                'Coûts de services de substitution'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />
          </Card>

          {/* Indemnisation */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <CheckCircleOutlined className="text-blue-500" />
              8. Indemnisation
            </Title>
            
            <Paragraph className="text-base">
              Vous acceptez de nous indemniser et de nous dégager de toute responsabilité concernant :
            </Paragraph>
            <List
              size="small"
              dataSource={[
                'Votre utilisation de nos services',
                'Violation de ces conditions d\'utilisation',
                'Violation des droits de tiers',
                'Réclamations liées à vos contenus ou données',
                'Utilisation non autorisée de votre compte'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />
          </Card>

          {/* Résiliation */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <ExclamationCircleOutlined className="text-orange-500" />
              9. Résiliation
            </Title>
            
            <Title level={4} className="mt-6 mb-3">9.1 Résiliation par vous</Title>
            <Paragraph className="text-base">
              Vous pouvez résilier votre compte à tout moment depuis les paramètres de votre compte 
              ou en nous contactant. La résiliation prend effet immédiatement.
            </Paragraph>

            <Title level={4} className="mt-6 mb-3">9.2 Résiliation par nous</Title>
            <Paragraph className="text-base">
              Nous pouvons résilier votre accès immédiatement en cas de :
            </Paragraph>
            <List
              size="small"
              dataSource={[
                'Violation grave de ces conditions',
                'Non-paiement des frais dus',
                'Activité frauduleuse ou illégale',
                'Utilisation abusive répétée',
                'Demande des autorités compétentes'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">9.3 Effets de la résiliation</Title>
            <List
              size="small"
              dataSource={[
                'Arrêt immédiat de l\'accès aux services',
                'Suppression des données après 30 jours (sauf obligation légale)',
                'Aucun remboursement des frais payés',
                'Survivance des clauses de propriété intellectuelle et de confidentialité'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />
          </Card>

          {/* Modifications */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <FileTextOutlined className="text-blue-500" />
              10. Modifications des conditions
            </Title>
            
            <Paragraph className="text-base">
              Nous nous réservons le droit de modifier ces conditions d'utilisation à tout moment. 
              Les modifications importantes seront notifiées par :
            </Paragraph>
            <List
              size="small"
              dataSource={[
                'E-mail à votre adresse enregistrée',
                'Notification dans votre tableau de bord',
                'Bannière sur notre site web',
                'Mise à jour de la date en haut de cette page'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />
            
            <Paragraph className="text-base mt-4">
              Votre utilisation continue de nos services après notification constitue votre acceptation 
              des conditions modifiées.
            </Paragraph>
          </Card>

          {/* Droit applicable */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <GlobalOutlined className="text-blue-500" />
              11. Droit applicable et juridiction
            </Title>
            
            <Paragraph className="text-base">
              Ces conditions d'utilisation sont régies par le droit ivoirien. 
              Tout litige relatif à ces conditions sera soumis à la juridiction exclusive 
              des tribunaux d'Abidjan, Côte d'Ivoire.
            </Paragraph>
            
            <Paragraph className="text-base">
              Avant tout recours judiciaire, nous encourageons la résolution amiable des différends 
              par médiation ou arbitrage.
            </Paragraph>
          </Card>

          {/* Dispositions générales */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <FileTextOutlined className="text-blue-500" />
              12. Dispositions générales
            </Title>
            
            <Title level={4} className="mt-6 mb-3">12.1 Intégralité de l'accord</Title>
            <Paragraph className="text-base">
              Ces conditions constituent l'intégralité de l'accord entre vous et Mercuri Shop 
              concernant l'utilisation de nos services.
            </Paragraph>

            <Title level={4} className="mt-6 mb-3">12.2 Divisibilité</Title>
            <Paragraph className="text-base">
              Si une disposition de ces conditions est jugée invalide, les autres dispositions 
              restent pleinement en vigueur.
            </Paragraph>

            <Title level={4} className="mt-6 mb-3">12.3 Renonciation</Title>
            <Paragraph className="text-base">
              Notre non-exercice d'un droit ne constitue pas une renonciation à ce droit.
            </Paragraph>

            <Title level={4} className="mt-6 mb-3">12.4 Cession</Title>
            <Paragraph className="text-base">
              Vous ne pouvez pas céder vos droits et obligations sans notre consentement écrit. 
              Nous pouvons céder cet accord dans le cadre d'une fusion, acquisition ou vente d'actifs.
            </Paragraph>
          </Card>

          {/* Contact */}
          <Card className="shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <MailOutlined className="text-blue-500" />
              13. Contact
            </Title>
            
            <Paragraph className="text-base">
              Pour toute question concernant ces conditions d'utilisation :
            </Paragraph>
            
            <Space direction="vertical" size="middle" className="w-full">
              <div className="flex items-center gap-3">
                <MailOutlined className="text-blue-500" />
                <Text strong>E-mail :</Text>
                <Text>shopmercuri@gmail.com</Text>
              </div>
              
              <div className="flex items-center gap-3">
                <PhoneOutlined className="text-blue-500" />
                <Text strong>Téléphone :</Text>
                <Text>+225 07 59 95 47 08</Text>
              </div>
              
              <div className="flex items-center gap-3">
                <GlobalOutlined className="text-blue-500" />
                <Text strong>Adresse :</Text>
                <Text>Mercuri Shop, Abidjan, Côte d'Ivoire</Text>
              </div>
            </Space>

            <Alert
              message="Service juridique"
              description="Pour les questions contractuelles complexes, contactez notre service juridique : shopmercuri@gmail.com"
              type="info"
              showIcon
              className="mt-6"
            />
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Terms; 